import React from 'react';
import { connect } from 'react-redux';
import { mapElementDrop, escapeKeyPress } from '../../actions/reduxActions/graph';
import Graph from './graph';
import Graphv2 from '../graph/Graph';
import { pathOr } from 'ramda';
import connectToStores from 'alt-store-connect';
import FlowStore from '../../stores/flow';
import GraphStore from '../../stores/graph';
import GraphActions from '../../actions/graph';
import FlowEditorHelp from './flow-editor-help';
import FlowEditorDetails from './flow-editor-details';
import FlowActions from '../../actions/flow';
import FlowEditorGettingStarted from './flow-editor-getting-started';
import MetadataEditor from '../metadata-editor';
import Actionbar from '../actionbar';
import classnames from 'classnames';
import SubFlowConfiguration from './mapelements/subflow/SubFlowConfiguration';
import Operator from './mapelements/operator/Operator';
import Swimlane from './mapelements/swimlane/Swimlane';
import GroupConfiguration from './mapelements/group/GroupConfiguration';
import GroupElementDelete from './mapelements/group/GroupElementDelete';
import { setFlow } from '../../actions/reduxActions/APIEndpoints';
import Actions from '../../config/flow-actions';
import { addNotification } from '../../actions/reduxActions/notification';
import { notificationTypes, MAP_ELEMENT_TYPES } from '../../constants';
import '../../../css/flow/editor.less';
import '../../../css/modal.less';
import '../../../css/flow/authorization-config.less';

import { saveMapElement, deleteMapElementFlowIdAssoc } from '../../actions/reduxActions/mapElements';
import { saveNote } from '../../actions/reduxActions/canvasNotes';
import { getFlag } from '../../config/flags';
import { mapObjectToSystemFlowInputs, generateIDs } from '../graph/utils';
import { safeToLower } from '../../utils/core';
import MapElementConfiguration from './mapelements/MapElementConfiguration';

@connectToStores
export class FlowEditor extends React.Component {
    static getStores() {
        return [FlowStore, GraphStore];
    }

    static getPropsFromStores(props) {
        return {
            flow: FlowStore.getState(),
            graph: GraphStore.getState().graphs[props.flowId],
        };
    }

    constructor(props) {
        super(props);
        this.state = {
            actions: [],
            isMetadataVisible: false,
            selectedMapElementId: null,
            selectedPlayer: 'default',
        };

        this.setSelectedPlayer = this.setSelectedPlayer.bind(this);
        this.setSelectedMapElement = this.setSelectedMapElement.bind(this);
        this.toggleMetadata = this.toggleMetadata.bind(this);
        this.openConfig = this.openConfig.bind(this);
        this.closeConfig = this.closeConfig.bind(this);
        this.onAction = this.onAction.bind(this);
        this.closeAction = this.closeAction.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.getDraggableElements = this.getDraggableElements.bind(this);
    }

    componentDidMount() {
        document.addEventListener('keydown', this.onKeyDown);

        // Chucking this call into the event queue to prevent collisions whilst dispatching AltJS actions
        // TODO: Migrate action bar state logic over into Redux
        setTimeout(() => FlowActions.setActions(Actions));
    }

    componentWillUpdate(nextProps) {
        if (this.props.isActive && this.props.graph) {
            const flow = this.props.flow.flows[this.props.flowId];
            setTimeout(() =>
                this.props.setFlow(
                    flow.id,
                    flow.versionId,
                    flow.developerName,
                    nextProps.graph.editingToken,
                ),
            );
        }
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.onKeyDown);
    }

    onAction(name) {
        if (this.state.actions.filter((action) => action.name === name).length === 0) {
            const actions = this.props.flow.actions.filter((action) => action.name === name)[0];
            this.setState({
                actions: this.state.actions.concat(actions),
            });
        }
    }

    onKeyDown(e) {
        if (this.props.isActive) {
            if (e.keyCode === 27) {
                if (this.props.flow.flows[this.props.flowId].config) {
                    this.closeConfig();
                }

                this.props.escapeKeyPress(this.props.flowId);
                if (getFlag('canvas') === false) {
                    // Only the old implementation uses this
                    this.updateGraph();
                }
            }

            const action = this.props.flow.actions.filter(
                (item) => item.shortcut && item.shortcut.code === e.keyCode,
            )[0];

            if (
                action &&
                e.ctrlKey === action.shortcut.control &&
                e.shiftKey === action.shortcut.shift &&
                e.altKey === action.shortcut.alt
            ) {
                e.preventDefault();
                this.onAction(action.name);
            }
        }
    }

    onGetUpdate = (updateGraph) => {
        this.updateGraph = updateGraph;
    };

    setSelectedPlayer(e) {
        this.setState({ selectedPlayer: e.target.value });
    }

    setSelectedMapElement(id, elementType) {
        this.setState({
            isMetadataVisible: true,
            selectedMapElementId: id,
            selectedMapElementType: elementType,
        });
    }

    getDraggableElements() {
        return pathOr([], ['current', 'refs', 'items', 'children'], this.props.sideBarRef);
    }

    saveReturnElement = (inputs, callbacks) => {
        this.props.saveMapElement(
            {
                ...inputs, 
                developerName: 'Return to Parent Flow',
            },
            this.props.flowId,
            this.props.graph.editingToken,
            this.props.currentUser.tenantId,
            this.props.currentUser.authenticationToken,
            () => {},
        );

        // Run all the callbacks because some cleanup tasks are done as callbacks e.g. removing the fake outcome
        callbacks.forEach((callback) => callback.execute());
    };

    openSubflowConfig = inputs => this.props.mapElementDrop({
        configComponent: SubFlowConfiguration,
        configProps: inputs,
        flowId: this.props.flowId,
    });

    openSwimlaneConfig = inputs => this.props.mapElementDrop({
        configComponent: Swimlane,
        configProps: inputs,
        flowId: this.props.flowId,
    });

    openGroupConfig = (inputs) => {
        // Map a flow inputs array into a standard JS object
        this.props.mapElementDrop({
            configComponent: GroupConfiguration,
            configProps: {
                ...inputs,
            },
            flowId: this.props.flowId,
        });
    };

    openOperatorConfig = (inputs) => {

        this.props.mapElementDrop({
            configComponent: Operator,
            configProps: {
                ...inputs,
            },
            flowId: this.props.flowId,
        });
    };

    openGroupElementDeleteConfig = (inputs) => {
        this.props.mapElementDrop({
            configComponent: GroupElementDelete,
            configProps: {
                ...inputs,
            },
            flowId: this.props.flowId,
        });
    };

    openNote = (inputs, callbacks) => {
        this.props.saveNote(
            {
                ...inputs, 
                developerName: 'New Note',
            },
            this.props.flowId,
            this.props.graph.editingToken,
            this.props.currentUser.tenantId,
            this.props.currentUser.authenticationToken,
            true,
        );

        callbacks.forEach(callback => callback.execute());
    }

    openConfig(flow, inputs, callbacks, options, mapElementId, element) {
        const configCallbacks = callbacks || [];
        configCallbacks.unshift({ execute: this.closeConfig, type: 'done' });

        if (element && safeToLower(element.id) === MAP_ELEMENT_TYPES.return) {
            this.saveReturnElement(generateIDs(inputs), callbacks);
            return;
        }
        
        if (element && safeToLower(element.id) === MAP_ELEMENT_TYPES.subflow) {
            this.openSubflowConfig(generateIDs(inputs));
            return;
        }

        if (element && safeToLower(element.id) === MAP_ELEMENT_TYPES.swimlane) {
            this.openSwimlaneConfig(generateIDs(inputs));
            return;
        }

        if (element && safeToLower(element.id) === MAP_ELEMENT_TYPES.group) {
            this.openGroupConfig(inputs);
            return;
        }

        if (flow === 'Delete Group Element') {
            this.openGroupElementDeleteConfig(inputs);
            return;
        }

        if(getFlag('operator-v2')) {
            if (element && safeToLower(element.id) === MAP_ELEMENT_TYPES.operator) {
                this.openOperatorConfig(inputs);
                return;
            }
        }

        if (element && safeToLower(element.id) === MAP_ELEMENT_TYPES.note) {
            this.openNote(generateIDs(inputs), callbacks);
            return;
        }
        
        // If we've got this far we're opening a System Flow

        // The new canvas code needs to reload when the config closes
        if (getFlag('canvas')) {

            // Bit of nastiness due to needing to call a redux action after
            // the delete map element system flow has done it's thing.
            if (flow === 'Delete Map Element' && safeToLower(inputs.elementType) === MAP_ELEMENT_TYPES.note) {
                configCallbacks.push({
                    execute: () => this.props.deleteMapElement(inputs.id),
                    type: 'done',
                });
            }

            configCallbacks.push({
                execute: () => GraphActions.get(this.props.flowId, this.props.currentUser.tenantId, this.props.currentUser.authenticationToken),
                type: 'done',
            });
        }

        // Open a System Flow editing panel
        FlowActions.openConfig({
            flowId: this.props.flowId,
            flow,
            inputs: mapObjectToSystemFlowInputs(inputs),
            callbacks: configCallbacks,
            options,
            mapElementId,
        });
    }

    closeConfig(callback, response) {
        const rootFaults = pathOr(null, ['mapElementInvokeResponses', 0, 'rootFaults'], response);

        if (response && rootFaults) {
            const rootFaultsAsJson = JSON.parse(rootFaults);
            Object.keys(rootFaults).forEach((fault) => {
                if (rootFaultsAsJson[fault]) {
                    const error = rootFaultsAsJson[fault];

                    this.props.addNotification({
                        type: notificationTypes.ERROR,
                        message: error.responseBody,
                    });
                }
            });
        }

        FlowActions.closeConfig(this.props.flowId);
    }

    closeAction(name) {
        this.setState({
            actions: this.state.actions.filter((action) => action.name !== name),
        });
    }

    toggleMetadata() {
        GraphActions.get(this.props.flowId, this.props.currentUser.tenantId, this.props.currentUser.authenticationToken);
        this.setState({ isMetadataVisible: !this.state.isMetadataVisible });
    }

    render() {
        const flow = this.props.flow.flows[this.props.flowId];
        const { config } = this.props.flow.flows[this.props.flowId];

        const { configPanel } = this.props;
        const configPanelFlowId = pathOr(null, ['flowId'], configPanel);
        const configPanelComponent = pathOr(null, ['component'], configPanel);
        const configPanelProps = pathOr(null, ['props'], configPanel);

        // To determine if there is an active config panel defined in redux state
        const isConfigActive = this.props.configPanel && this.props.flowId === configPanelFlowId;

        const actions = this.props.flow.actions.map(action => ({
            name: action.name,
            onClick: this.onAction.bind(null, action.name),
            icon: action.icon,
            tooltip: action.tooltip,
        }));

        let gettingStarted = null;
        if (
            this.props.graph &&
            this.props.graph.mapElements.length === 1 &&
            (!this.props.graph.groupElements || this.props.graph.groupElements.length === 0)
        ) {
            gettingStarted = <FlowEditorGettingStarted />;
        }

        const classes = classnames({
            'flow-editor': true,
            admin: true,
            active: this.props.isActive,
        });

        return (
            <div className={classes}>
                {
                    getFlag('canvas') ?
                        (
                            <Graphv2
                                setSelectedMapElement={this.setSelectedMapElement}
                                flowId={this.props.flowId}
                                openConfig={this.openConfig}
                                editingToken={this.props.graph && this.props.graph.editingToken}
                                showBackdrop={config || isConfigActive}
                            />
                        ) :
                        (
                            <div className="flow-editor-canvas-wrapper">
                                <Graph
                                    setSelectedMapElement={this.setSelectedMapElement}
                                    disabled={this.state.actions.length > 0 || config || isConfigActive}
                                    isActive={this.props.isActive}
                                    getDraggableElements={this.getDraggableElements}
                                    flowId={this.props.flowId}
                                    isMetadataVisible={this.state.isMetadataVisible}
                                    tenantId={this.props.currentUser.tenantId}
                                    authenticationToken={this.props.currentUser.authenticationToken}
                                    openConfig={this.openConfig}
                                    username={this.props.currentUser.username}
                                    onGetUpdate={this.onGetUpdate}
                                />
                            </div>
                        )
                }
                {
                    this.state.actions.map(action => React.createElement(action.component, {
                        container: this,
                        flowId: this.props.flowId,
                        tenantId: this.props.currentUser.tenantId,
                        editorId: this.props.id,
                        authenticationToken: this.props.currentUser.authenticationToken,
                        close: this.closeAction.bind(null, action.name),
                        adminTenantId: manywho.settings.global('adminTenantId'),
                        key: action.name,
                        selectedPlayer: this.state.selectedPlayer,
                        setSelectedPlayer: this.setSelectedPlayer,
                    }),
                )}

                <Actionbar actions={actions} container={this} />
                {gettingStarted}

                <FlowEditorHelp isActive={this.props.isActive} />

                <FlowEditorDetails
                    id={flow.id}
                    versionId={flow.versionId}
                    dateModified={this.props.graph && this.props.graph.dateModified}
                />

                <MapElementConfiguration 
                    config={config}
                    isConfigActive={isConfigActive}
                    tenantId={this.props.currentUser.tenantId}
                    authenticationToken={this.props.currentUser.authenticationToken}
                    configPanelComponent={configPanelComponent}
                    configPanelProps={configPanelProps}
                    container={this}
                />

                <MetadataEditor
                    container={this}
                    isVisible={this.state.isMetadataVisible}
                    toggleMetadata={this.toggleMetadata}
                    id={this.state.selectedMapElementId}
                    modelId={this.props.id}
                    elementType={this.state.selectedMapElementType}
                    flowId={this.props.flowId}
                    editingToken={this.props.graph && this.props.graph.editingToken}
                    tenantId={this.props.currentUser.tenantId}
                    authenticationToken={this.props.currentUser.authenticationToken}
                />
            </div>
        );
    }
}

export default connect(
    ({ configPanel, mapElements, currentUser }, ownProps) => ({
        currentUser,
        mapElements,
        configPanel: configPanel.find((config) => config.flowId === ownProps.flowId),
    }),
    {
        mapElementDrop,
        escapeKeyPress,
        saveMapElement,
        setFlow,
        addNotification,
        saveNote,
        deleteMapElement: deleteMapElementFlowIdAssoc,
    },
)(FlowEditor);
